import { useState } from 'react';
import { alertLeadDays } from '../utils.js';
import { Plus, Edit2, Trash2, Eye, Paperclip, Search, MessageSquare, X, Settings2, RefreshCw, History } from 'lucide-react';
import { FileActions } from '../../deskte-file-utils.jsx';
import { toast } from './Toast.jsx';
import { LicenseForm, getLicenseTypes, useAgencyLicenseTypes, parseLicenseNotes, idDeCategoria } from './LicenseForm.jsx';
import { renovarLicencia, archivoDeVersionDeLicencia } from '../api.js';
import { confirmDialog, RichNote, Modal, ImageLightbox } from './ui.jsx';

export function LicensesView({ licenses, onLicensesChange, onSaveLicense, onDeleteLicense, currentUser, lang, userId, readOnly, userPrefs = {} }) {
  // Mismo plazo que el aviso: la pestana "Por Vencer" usaba 90 dias fijos, el
  // numerito del menu 60 y el aviso otro. Tres numeros distintos para la misma
  // pregunta (Lissette 2026-08-17). Ahora manda el de Preferencias -> Avisos.
  const licLead = alertLeadDays(userPrefs, 'license');
  // Le faltan horas continuas y hay fecha limite: el mismo criterio del aviso.
  const ceMissing = (l) => {
    const req = parseFloat(l?.ceRequired), done = parseFloat(l?.ceCredits) || 0;
    return Number.isFinite(req) && req > 0 && done < req && !!l?.ceDeadline;
  };
  const targetUserId = userId || currentUser?.id;
  const [showForm, setShowForm] = useState(false);
  const [editLic, setEditLic] = useState(null);
  const [filter, setFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [viewFileId, setViewFileId] = useState(null);
  const [notesFor, setNotesFor] = useState(null); // license whose notes bubble is open
  const [lightbox, setLightbox] = useState(null); // { src, name } open image viewer
  // RENOVAR EN LA MISMA TARJETA + HISTORIAL (Lissette 2026-10-20).
  const [renovarLic, setRenovarLic] = useState(null);   // la licencia que se esta renovando
  const [historialAbierto, setHistorialAbierto] = useState({}); // { [licId]: true }
  const [archivosViejos, setArchivosViejos] = useState({}); // { 'licId:n': {name,data,type} | 'cargando' }

  // Categorias propias de la agencia (Lissette 2026-09-02, queja 7), ademas de
  // las de fabrica. Se guardan en los ajustes de la agencia, asi que se quedan.
  const [tiposPropios, guardarTiposPropios] = useAgencyLicenseTypes();
  const [gestorDeTipos, setGestorDeTipos] = useState(false);
  const LICENSE_TYPES = getLicenseTypes(lang, tiposPropios);
  // La reserva es "Otro", POR NOMBRE.
  //
  // Era `LICENSE_TYPES[5]`, o sea una POSICION — y la posicion 5 no es "Otro",
  // es "Certificacion". Una licencia con un tipo que ya no existe salia
  // etiquetada como certificacion, que es una categoria real y equivocada, en
  // vez de como "Otro". Con categorias propias, que se pueden borrar, ese caso
  // deja de ser raro: borrar una categoria dejaria a sus licencias marcadas
  // como certificaciones.
  const TIPO_OTRO = LICENSE_TYPES.find(l => l.id === 'other') || LICENSE_TYPES[LICENSE_TYPES.length - 1];
  const getLicType = (id) => LICENSE_TYPES.find(l=>l.id===id) || TIPO_OTRO;

  // Whole-calendar-day diff in LOCAL time. A date-only string ('YYYY-MM-DD')
  // parses as UTC midnight, so `new Date(d)-new Date()` was off by the UTC
  // offset (Florida -4/-5h) and could flag a license expired/expiring a day
  // early or late (feedback_local_day_not_utc). Compare local midnights.
  const daysUntilExpiry = (d) => {
    if(!d) return null;
    const target = /^\d{4}-\d{2}-\d{2}$/.test(d) ? new Date(d + 'T00:00:00') : new Date(d);
    if(isNaN(target.getTime())) return null;
    const now = new Date();
    const a = new Date(target.getFullYear(), target.getMonth(), target.getDate());
    const b = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return Math.round((a - b) / 86400000);
  };

  const getStatusInfo = (lic) => {
    const days = daysUntilExpiry(lic.expiryDate);
    if (days === null) return { label:lang==="es"?"Sin fecha":"No date", color:"#64748b", bg:"rgba(100,116,139,.12)" };
    if (days < 0) return { label:lang==="es"?"Vencida":"Expired", color:"#ef4444", bg:"rgba(239,68,68,.12)" };
    if (days <= 30) return { label:`${days}d ⚠️`, color:"#ef4444", bg:"rgba(239,68,68,.12)" };
    if (days <= 60) return { label:`${days}d`, color:"#ffb700", bg:"rgba(255,183,0,.12)" };
    if (days <= 90) return { label:`${days}d`, color:"#f97316", bg:"rgba(249,115,22,.12)" };
    return { label:lang==="es"?"Vigente":"Active", color:"#00ff88", bg:"rgba(0,255,136,.12)" };
  };

  const userLicenses = licenses.filter(l => !targetUserId || l.userId === targetUserId);
  const q = search.trim().toLowerCase();
  const filtered = userLicenses.filter(l => {
    if (filter === "expiring") { const d = daysUntilExpiry(l.expiryDate); if (!(d !== null && d >= 0 && d <= licLead)) return false; }
    else if (filter === "expired") { const d = daysUntilExpiry(l.expiryDate); if (!(d !== null && d < 0)) return false; }
    // "Vigentes" = TODO lo que NO ha vencido, incluidas las que estan por
    // vencer: una licencia que vence en 20 dias SIGUE VIGENTE, y sacarla de
    // esta pestana se lee como que ya no vale (Lissette 2026-08-17: "no me
    // muestra la vigente en vigente aun se esta venciendo"). "Por Vencer" es
    // un subconjunto suyo -las que hay que atender-, no una cosa aparte.
    else if (filter === "active") { const d = daysUntilExpiry(l.expiryDate); if (!(d === null || d >= 0)) return false; }
    else if (filter === "ce") { if (!ceMissing(l)) return false; }
    if (typeFilter !== "all" && l.type !== typeFilter) return false;
    if (q) {
      const noteText = parseLicenseNotes(l.notes).map((n) => n.text || "").join(" ");
      const blob = `${l.licenseNumber || ""} ${l.state || ""} ${l.npn || ""} ${l.carrier || ""} ${noteText}`.toLowerCase();
      if (!blob.includes(q)) return false;
    }
    return true;
  }).sort((a,b) => (a.expiryDate||"9999").localeCompare(b.expiryDate||"9999"));

  // Server-backed: deskte-full persists via the licenses API then patches
  // local state. (Was localStorage-only → didn't sync across devices.)
  const saveLic = (form) => {
    onSaveLicense && onSaveLicense(editLic ? { ...form, id: editLic.id } : form);
    setShowForm(false); setEditLic(null);
  };
  const deleteLic = (id) => { onDeleteLicense && onDeleteLicense(id); };

  // "+ Nuevo tipo…" desde el formulario: misma categoria propia que el gestor.
  // Si ya existe una con ese nombre (de fabrica o propia), se usa esa.
  const crearTipoDesdeElFormulario = async (nombre) => {
    const id = idDeCategoria(nombre);
    if (!id) { toast.warn(lang === "es" ? "Ese nombre no deja hacer un tipo." : "That name cannot make a type."); return null; }
    const igual = LICENSE_TYPES.find(t => t.id === id || String(t.label || "").toLowerCase() === nombre.trim().toLowerCase());
    if (igual) return igual.id;
    await guardarTiposPropios([...tiposPropios, { id, label: nombre.trim(), labelEs: nombre.trim(), icon: '🪪', color: '#6366f1' }]);
    return id;
  };

  // Lo que sale ya puesto al renovar: lo que no cambia (tipo, estado, numero,
  // NPN, compania, horas requeridas). Fechas, horas hechas y archivo, en blanco:
  // son de la version nueva.
  const abrirRenovacion = (lic) => setRenovarLic({
    ...lic, issuedDate: "", expiryDate: "", ceCredits: "", ceDeadline: "", appointmentDate: "",
    fileData: "", fileName: "", fileType: "",
  });
  const guardarRenovacion = async (form) => {
    const lic = renovarLic;
    if (!lic) return;
    try {
      const fila = await renovarLicencia(lic.id, form);
      onLicensesChange && onLicensesChange(p => p.map(l => l.id === fila.id ? fila : l));
      setHistorialAbierto(h => ({ ...h, [fila.id]: true }));
      toast.success(lang === "es" ? "Renovada. La anterior quedó en el historial." : "Renewed. The previous one is in the history.");
      setRenovarLic(null);
    } catch (e) {
      toast.error((lang === "es" ? "No se pudo renovar: " : "Could not renew: ") + (e.message || e));
    }
  };
  const abrirArchivoViejo = async (lic, n) => {
    const k = `${lic.id}:${n}`;
    setArchivosViejos(a => ({ ...a, [k]: 'cargando' }));
    try {
      const f = await archivoDeVersionDeLicencia(lic.id, n);
      setArchivosViejos(a => ({ ...a, [k]: { name: f.fileName, data: f.fileData, type: f.fileType || "" } }));
    } catch (e) {
      setArchivosViejos(a => { const x = { ...a }; delete x[k]; return x; });
      toast.error((lang === "es" ? "No se pudo abrir el archivo: " : "Could not open the file: ") + (e.message || e));
    }
  };

  // ── Expiry alerts (90 / 60 / 30 / 5 / 1 day) ────────────────────────────
  // Pick the TIGHTEST window each license is currently in. Dismissals are
  // stored per-license in `dismissedAlerts` (e.g. {"90": true}) so each
  // checkpoint stops re-appearing after one click. A renewal upload resets
  // the map (handled by the backend when expiryDate changes).
  const ALERT_DAYS = [1, 5, 30, 60, 90];
  const alerts = userLicenses.map(l => {
    const d = daysUntilExpiry(l.expiryDate);
    if (d === null || d < 0) return null;
    const dismissed = (l.dismissedAlerts && typeof l.dismissedAlerts === "object") ? l.dismissedAlerts : {};
    const window = ALERT_DAYS.find(w => d <= w && !dismissed[String(w)]);
    if (!window) return null;
    return { license: l, days: d, window };
  }).filter(Boolean).sort((a, b) => a.days - b.days);

  // Aviso de HORAS CONTINUAS. El panel de arriba solo miraba el vencimiento de
  // la licencia, asi que las horas -que tambien te dejan sin poder trabajar- no
  // salian por ningun lado salvo entrando a mirar la barrita
  // (Lissette 2026-08-17). Mismo criterio que el aviso general: solo si de
  // verdad FALTAN horas y la fecha limite esta cerca o ya paso.
  const ceAlerts = userLicenses.map(l => {
    if (!ceMissing(l)) return null;
    const d = daysUntilExpiry(l.ceDeadline);
    if (d === null || d > licLead) return null;
    const req = parseFloat(l.ceRequired), done = parseFloat(l.ceCredits) || 0;
    return { license: l, days: d, faltan: Math.round((req - done) * 10) / 10, req };
  }).filter(Boolean).sort((a, b) => a.days - b.days);

  const dismissAlert = (lic, windowKey) => {
    if (!onSaveLicense) return;
    const next = { ...(lic.dismissedAlerts && typeof lic.dismissedAlerts === "object" ? lic.dismissedAlerts : {}), [String(windowKey)]: true };
    onSaveLicense({ ...lic, dismissedAlerts: next });
  };
  const renewAlert = (lic) => {
    // Reuse the edit form so the agent can update the expiry date and
    // upload the new file in one place. When they save with a different
    // expiryDate the backend resets dismissedAlerts to {}.
    setEditLic(lic);
    setShowForm(true);
  };
  const fmtDays = (d) => d <= 0 ? (lang === "es" ? "vence HOY" : "expires TODAY")
    : d === 1 ? (lang === "es" ? "vence MAÑANA" : "expires TOMORROW")
    : lang === "es" ? `en ${d} días` : `in ${d} days`;


  const expiringCount = userLicenses.filter(l => { const d = daysUntilExpiry(l.expiryDate); return d !== null && d >= 0 && d <= licLead; }).length;
  const expiredCount = userLicenses.filter(l => { const d = daysUntilExpiry(l.expiryDate); return d !== null && d < 0; }).length;
  const activeCount = userLicenses.filter(l => { const d = daysUntilExpiry(l.expiryDate); return d === null || d >= 0; }).length;
  const ceCount = userLicenses.filter(ceMissing).length;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "1rem", flexWrap: "wrap", gap: ".5rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: ".5rem" }}>
          <span style={{ fontSize: "1.4rem" }}>🪪</span>
          <h2 style={{ fontSize: "1.1rem", fontWeight: 800, color: "var(--c-white,#eef6ff)", margin: 0 }}>{lang === "es" ? "Mis Licencias y Certificaciones" : "My Licenses & Certifications"}</h2>
          <span style={{ fontSize: ".75rem", color: "var(--c-text3,#45688a)", background: "var(--c-surface2,#112538)", padding: "2px 10px", borderRadius: 100, fontWeight: 700, border: "1px solid var(--c-border,#1a3550)" }}>{userLicenses.length}</span>
          {expiringCount > 0 && <span style={{ fontSize: ".68rem", padding: "2px 8px", borderRadius: 100, fontWeight: 700, background: "rgba(255,183,0,.12)", color: "#ffb700", border: "1px solid rgba(255,183,0,.25)" }}>⚠️ {expiringCount} {lang === "es" ? "por vencer" : "expiring"}</span>}
          {expiredCount > 0 && <span style={{ fontSize: ".68rem", padding: "2px 8px", borderRadius: 100, fontWeight: 700, background: "rgba(239,68,68,.12)", color: "#ef4444", border: "1px solid rgba(239,68,68,.25)" }}>🔴 {expiredCount} {lang === "es" ? "vencidas" : "expired"}</span>}
        </div>
        <div style={{display:"flex",gap:".4rem",flexWrap:"wrap"}}>
          {!readOnly && (
            <button type="button" onClick={() => { setEditLic(null); setShowForm(true); }}
              style={{ display: "flex", alignItems: "center", gap: 5, background: "linear-gradient(135deg,#0099cc,#0055aa)", border: "none", color: "#fff", fontSize: ".78rem", fontWeight: 700, padding: ".5rem 1rem", borderRadius: 9, cursor: "pointer", fontFamily: "inherit" }}>
              <Plus size={14} />{lang === "es" ? "Nuevo" : "New"}
            </button>
          )}
          {readOnly && (
            <span style={{display:"flex",alignItems:"center",gap:5,background:"rgba(0,212,255,.1)",border:"1px solid rgba(0,212,255,.3)",color:"var(--c-cyan,#00d4ff)",fontSize:".7rem",fontWeight:700,padding:".4rem .8rem",borderRadius:9}}>
              <Eye size={12}/> {lang === "es" ? "Solo lectura" : "View only"}
            </span>
          )}
        </div>
      </div>

      {ceAlerts.length > 0 && (
        <div style={{ marginBottom: ".5rem", display: "flex", flexDirection: "column", gap: ".4rem" }}>
          {ceAlerts.map(({ license, days, faltan, req }) => {
            const vencida = days < 0;
            const fg = vencida ? "#ef4444" : "#ffb700";
            const quien = [license.state, license.licenseNumber].filter(Boolean).join(" ") || (lang === "es" ? "Licencia" : "License");
            return (
              <div key={"ce" + license.id} style={{ display: "flex", alignItems: "center", gap: ".6rem", padding: ".5rem .7rem", borderRadius: 9, background: vencida ? "rgba(239,68,68,.10)" : "rgba(255,183,0,.10)", border: `1px solid ${vencida ? "rgba(239,68,68,.35)" : "rgba(255,183,0,.35)"}` }}>
                <span style={{ fontSize: "1rem" }}>{vencida ? "🚨" : "⏰"}</span>
                <div style={{ flex: 1, minWidth: 0, fontSize: ".76rem", color: "var(--c-text,#c8dff0)" }}>
                  <b style={{ color: fg }}>{lang === "es" ? "Horas continuas" : "Continuing education"}</b>{" — "}
                  {quien}: {lang === "es"
                    ? `faltan ${faltan} de ${req} horas`
                    : `${faltan} of ${req} hours missing`}
                  {vencida
                    ? (lang === "es" ? " y la fecha límite ya pasó" : " and the deadline has passed")
                    : (lang === "es" ? ` — vence en ${days} día${days === 1 ? "" : "s"}` : ` — due in ${days} day${days === 1 ? "" : "s"}`)}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {alerts.length > 0 && !readOnly && (
        <div style={{ marginBottom: ".75rem", display: "flex", flexDirection: "column", gap: ".4rem" }}>
          {alerts.map(({ license, days, window }) => {
            const lt = getLicType(license.type);
            const urgent = days <= 5;
            const bg = urgent ? "rgba(239,68,68,.10)" : "rgba(255,183,0,.10)";
            const fg = urgent ? "#ef4444" : "#ffb700";
            const bd = urgent ? "rgba(239,68,68,.35)" : "rgba(255,183,0,.35)";
            return (
              <div key={`${license.id}-${window}`}
                style={{ display: "flex", alignItems: "center", gap: ".6rem", padding: ".6rem .8rem", background: bg, border: `1px solid ${bd}`, borderRadius: 9, flexWrap: "wrap" }}>
                <span style={{ fontSize: "1.1rem" }}>{urgent ? "🚨" : "⚠️"}</span>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontSize: ".78rem", fontWeight: 800, color: fg }}>
                    {lt.label} · {license.state || "—"} · #{license.licenseNumber || "—"}
                  </div>
                  <div style={{ fontSize: ".7rem", color: "var(--c-text2,#7aa3c0)", marginTop: 1 }}>
                    {lang === "es" ? "Vence" : "Expires"} {fmtDays(days)} ({license.expiryDate}) · {lang === "es" ? "umbral" : "threshold"} {window}d
                  </div>
                </div>
                <button type="button" onClick={() => renewAlert(license)}
                  style={{ display: "flex", alignItems: "center", gap: 4, padding: ".3rem .7rem", borderRadius: 7, border: `1px solid ${bd}`, background: "transparent", color: fg, fontSize: ".7rem", fontWeight: 800, cursor: "pointer", fontFamily: "inherit" }}
                  title={lang === "es" ? "Cargar licencia renovada" : "Upload renewed license"}>
                  <Paperclip size={11}/> {lang === "es" ? "Renovar / Subir" : "Renew / Upload"}
                </button>
                <button type="button" onClick={() => dismissAlert(license, window)}
                  style={{ padding: ".3rem .6rem", borderRadius: 7, border: "1px solid var(--c-border,#1a3550)", background: "var(--c-surface2,#112538)", color: "var(--c-text2,#7aa3c0)", fontSize: ".7rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}
                  title={lang === "es" ? "Descartar este aviso" : "Dismiss this alert"}>
                  {lang === "es" ? "Descartar" : "Dismiss"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ display: "flex", gap: ".5rem", marginBottom: ".5rem", alignItems: "center", flexWrap: "wrap" }}>
        <div style={{ position: "relative", flex: "1 1 220px", minWidth: 200 }}>
          <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--c-text3,#45688a)", pointerEvents: "none" }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={lang === "es" ? "Buscar (número, estado, NPN, carrier)…" : "Search (number, state, NPN, carrier)…"}
            className="dkt-search"
            style={{ paddingLeft: 32, width: "100%" }}
          />
        </div>
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value)}
          style={{ padding: ".4rem .65rem", borderRadius: 8, border: "1px solid var(--c-border,#1a3550)", background: "var(--c-surface2,#112538)", color: "var(--c-white,#eef6ff)", fontSize: ".75rem", fontWeight: 600, fontFamily: "inherit", cursor: "pointer", minWidth: 160 }}
          title={lang === "es" ? "Filtrar por tipo" : "Filter by type"}
        >
          <option value="all">{lang === "es" ? "Todos los tipos" : "All types"}</option>
          {LICENSE_TYPES.map(lt => (
            <option key={lt.id} value={lt.id}>{lt.icon} {lt.label}</option>
          ))}
        </select>
        {/* Gestionar las categorias propias. Al lado del filtro de tipo porque
            es donde se descubre que falta una (Lissette 2026-09-02, queja 7). */}
        {!readOnly && (
          <button
            type="button"
            onClick={() => setGestorDeTipos(true)}
            title={lang === "es" ? "Crear o quitar categorías de la agencia" : "Create or remove agency categories"}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: ".4rem .65rem", borderRadius: 8, border: "1px solid var(--c-border,#1a3550)", background: "var(--c-surface2,#112538)", color: "var(--c-text2,#7aa3c0)", fontSize: ".75rem", fontWeight: 600, fontFamily: "inherit", cursor: "pointer" }}
          >
            <Settings2 size={13} />{lang === "es" ? "Categorías" : "Categories"}
          </button>
        )}
      </div>

      <div style={{ display: "flex", gap: ".35rem", marginBottom: ".75rem", flexWrap: "wrap" }}>
        {[
          { id: "all", label: lang === "es" ? "Todas" : "All", n: userLicenses.length },
          { id: "active", label: lang === "es" ? "Vigentes" : "Active", n: activeCount },
          { id: "expiring", label: lang === "es" ? "Por Vencer" : "Expiring", n: expiringCount, warn: true },
          { id: "expired", label: lang === "es" ? "Vencidas" : "Expired", n: expiredCount, danger: true },
          { id: "ce", label: lang === "es" ? "Horas continuas pendientes" : "Continuing-education hours due", n: ceCount, warn: true },
        ].map(s => (
          <button type="button" key={s.id} onClick={() => setFilter(s.id)}
            style={{ padding: ".3rem .7rem", borderRadius: 100, border: `1px solid ${filter === s.id ? "rgba(0,212,255,.4)" : "var(--c-border,#1a3550)"}`, background: filter === s.id ? "var(--c-cyan-dim)" : "transparent", color: filter === s.id ? "var(--c-cyan,#00d4ff)" : "var(--c-text3,#45688a)", fontSize: ".7rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
            {s.label}{s.n > 0 ? ` (${s.n})` : ""}
          </button>
        ))}
      </div>

      {filtered.length === 0 && <div style={{ textAlign: "center", padding: "2.5rem", color: "var(--c-text3,#45688a)", fontSize: ".82rem" }}>{lang === "es" ? "No hay licencias registradas" : "No licenses registered"}</div>}

      <div style={{ display: "flex", flexDirection: "column", gap: ".5rem" }}>
        {filtered.map(lic => {
          const lt = getLicType(lic.type);
          const st = getStatusInfo(lic);
          const ceProgress = (lic.ceCredits && lic.ceRequired) ? Math.min(100, Math.round((parseFloat(lic.ceCredits) / parseFloat(lic.ceRequired)) * 100)) : null;
          const noteList = parseLicenseNotes(lic.notes);
          return (
            <div key={lic.id} style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderLeft: `3px solid ${st.color}`, borderRadius: 10, padding: ".875rem 1rem" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: ".5rem" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: ".4rem", flexWrap: "wrap", marginBottom: ".3rem" }}>
                    <span style={{ fontSize: "1rem" }}>{lt.icon}</span>
                    <span style={{ fontWeight: 700, fontSize: ".88rem", color: "var(--c-white,#eef6ff)" }}>{lt.label}</span>
                    <span style={{ fontSize: ".65rem", padding: "2px 8px", borderRadius: 100, fontWeight: 700, background: "var(--c-surface3,#162d44)", color: "var(--c-cyan,#00d4ff)", border: "1px solid var(--c-border,#1a3550)" }}>{lic.state}</span>
                    <span style={{ fontSize: ".65rem", padding: "2px 8px", borderRadius: 100, fontWeight: 700, background: st.bg, color: st.color, border: `1px solid ${st.color}33` }}>{st.label}</span>
                  </div>
                  <div style={{ display: "flex", gap: ".75rem", flexWrap: "wrap", fontSize: ".72rem", color: "var(--c-text3,#45688a)" }}>
                    <span><span style={{ fontFamily: "var(--font-mono)", color: "var(--c-text2,#7aa3c0)", fontWeight: 600 }}># {lic.licenseNumber}</span></span>
                    {lic.npn && <span>NPN: <span style={{ color: "var(--c-text2,#7aa3c0)", fontWeight: 600 }}>{lic.npn}</span></span>}
                    {lic.issuedDate && <span>📅 {lic.issuedDate}</span>}
                    <span>⏰ {lic.expiryDate || "--"}</span>
                    {lic.carrier && <span>🏢 {lic.carrier}</span>}
                  </div>
                  {ceProgress !== null && (
                    <div style={{ marginTop: ".45rem", display: "flex", alignItems: "center", gap: ".5rem" }}>
                      <span style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", fontWeight: 700, fontFamily: "var(--font-mono)" }}>📚 CE:</span>
                      <div style={{ flex: 1, maxWidth: 180, height: 8, background: "var(--c-surface3,#162d44)", borderRadius: 4, overflow: "hidden" }}>
                        <div style={{ width: ceProgress + "%", height: "100%", background: ceProgress >= 100 ? "var(--c-green,#00ff88)" : ceProgress >= 50 ? "var(--c-amber,#ffb700)" : "var(--c-red,#ff3d6b)", borderRadius: 4, transition: "width .3s" }} />
                      </div>
                      <span style={{ fontSize: ".62rem", color: ceProgress >= 100 ? "var(--c-green,#00ff88)" : "var(--c-text2,#7aa3c0)", fontWeight: 700, fontFamily: "var(--font-mono)" }}>{lic.ceCredits}/{lic.ceRequired} ({ceProgress}%)</span>
                      {lic.ceDeadline && <span style={{ fontSize: ".6rem", color: "var(--c-text3,#45688a)" }}>⏰ {lic.ceDeadline}</span>}
                    </div>
                  )}
                  {lic.fileData && (
                    <div style={{ marginTop: ".4rem", display: "flex", alignItems: "center", gap: ".4rem", flexWrap:"wrap" }}>
                      <span style={{ display:"flex", alignItems:"center", gap:4, fontSize:".65rem", color:"var(--c-text3,#45688a)", fontFamily:"var(--font-mono)" }}>
                        <Paperclip size={11} />{lic.fileName}
                      </span>
                      <FileActions
                        file={{ name:lic.fileName, data:lic.fileData, type:lic.fileType||"", size:null }}
                        lang={lang}
                        sendSubject={`${lang==="es"?"Licencia":"License"} — ${lic.state} #${lic.licenseNumber}`}
                        sendBody={`${lang==="es"?"Adjunto mi licencia de seguros":"Please find my insurance license attached"}:\n\n${lang==="es"?"Estado":"State"}: ${lic.state}\n${lang==="es"?"Número":"Number"}: ${lic.licenseNumber}\nNPN: ${lic.npn||"--"}\n${lang==="es"?"Vence":"Expires"}: ${lic.expiryDate||"--"}\n\n— Deskte CRM`}
                      />
                    </div>
                  )}
                  {/* HISTORIAL: las versiones anteriores de esta licencia, la mas
                      nueva primero. El archivo se pide al abrirlo (pesa). */}
                  {Array.isArray(lic.history) && lic.history.length > 0 && (
                    <div style={{ marginTop: ".45rem" }}>
                      <button type="button" onClick={() => setHistorialAbierto(h => ({ ...h, [lic.id]: !h[lic.id] }))}
                        style={{ display: "inline-flex", alignItems: "center", gap: 5, background: "none", border: "none", padding: 0, color: "var(--c-cyan,#00d4ff)", fontSize: ".7rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                        <History size={12} /> {lang === "es" ? `Historial (${lic.history.length})` : `History (${lic.history.length})`}
                      </button>
                      {historialAbierto[lic.id] && (
                        <div style={{ marginTop: ".35rem", display: "flex", flexDirection: "column", gap: ".3rem" }}>
                          {lic.history.map((v, n) => {
                            const k = `${lic.id}:${n}`;
                            const archivo = archivosViejos[k];
                            return (
                              <div key={k} style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 8, padding: ".4rem .6rem", fontSize: ".68rem", color: "var(--c-text3,#45688a)" }}>
                                <div style={{ display: "flex", gap: ".6rem", flexWrap: "wrap" }}>
                                  {v.licenseNumber && <span style={{ fontFamily: "var(--font-mono)", color: "var(--c-text2,#7aa3c0)" }}># {v.licenseNumber}</span>}
                                  {v.state && <span>{v.state}</span>}
                                  {v.issuedDate && <span>📅 {v.issuedDate}</span>}
                                  <span>⏰ {v.expiryDate || "--"}</span>
                                  {(v.ceCredits || v.ceRequired) && <span>📚 {v.ceCredits || 0}/{v.ceRequired || "?"}</span>}
                                  {v.archivadaEl && <span>{lang === "es" ? "Reemplazada el" : "Replaced on"} {new Date(v.archivadaEl).toLocaleDateString("en-US")}{v.archivadaPor ? ` · ${v.archivadaPor}` : ""}</span>}
                                </div>
                                {v.tieneArchivo && (
                                  <div style={{ marginTop: ".3rem", display: "flex", alignItems: "center", gap: ".4rem", flexWrap: "wrap" }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: 4, fontFamily: "var(--font-mono)" }}><Paperclip size={10} />{v.fileName || (lang === "es" ? "archivo" : "file")}</span>
                                    {archivo && archivo !== 'cargando'
                                      ? <FileActions file={{ name: archivo.name, data: archivo.data, type: archivo.type, size: null }} lang={lang} />
                                      : <button type="button" className="dkt-btn xs ghost" disabled={archivo === 'cargando'} onClick={() => abrirArchivoViejo(lic, n)}>
                                          <Eye size={11} /> {archivo === 'cargando' ? (lang === "es" ? "Cargando…" : "Loading…") : (lang === "es" ? "Abrir" : "Open")}
                                        </button>}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                {/* Burbuja de notas: clic → abre TODAS las notas en un visor,
                    y las fotos se abren dentro de la app (Lissette 2026-07-28). */}
                {noteList.length > 0 && (
                  <button type="button" onClick={() => setNotesFor(lic)} title={lang === "es" ? "Ver notas" : "View notes"}
                    style={{ display: "inline-flex", alignItems: "center", gap: 5, alignSelf: "flex-start", flexShrink: 0, background: "var(--c-surface3,#162d44)", border: "1px solid rgba(0,212,255,.35)", color: "var(--c-cyan,#00d4ff)", borderRadius: 100, padding: "4px 11px", fontSize: ".72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                    <MessageSquare size={13} /> {noteList.length}
                  </button>
                )}
                {!readOnly && (
                  <div style={{ display: "flex", gap: ".3rem", flexShrink: 0 }}>
                    {onSaveLicense && (
                      <button type="button" onClick={() => abrirRenovacion(lic)} title={lang === "es" ? "Renovar: subir la nueva y guardar la anterior en el historial" : "Renew: upload the new one and keep the old one in the history"}
                        style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "var(--c-cyan-dim)", border: "1px solid rgba(0,212,255,.35)", color: "var(--c-cyan,#00d4ff)", cursor: "pointer", padding: "4px 8px", borderRadius: 7, fontSize: ".68rem", fontWeight: 700, fontFamily: "inherit" }}>
                        <RefreshCw size={11} /> {lang === "es" ? "Renovar" : "Renew"}
                      </button>
                    )}
                    <button type="button" onClick={() => { setEditLic(lic); setShowForm(true); }}
                      style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text2,#7aa3c0)", cursor: "pointer", padding: "4px 7px", borderRadius: 7, lineHeight: 0 }}><Edit2 size={12} /></button>
                    <button type="button" onClick={async () => { if (await confirmDialog({ message: lang === "es" ? "¿Eliminar licencia?" : "Delete license?", danger: true })) deleteLic(lic.id); }}
                      style={{ background: "var(--c-surface3,#162d44)", border: "1px solid var(--c-border,#1a3550)", color: "var(--c-text3,#45688a)", cursor: "pointer", padding: "4px 7px", borderRadius: 7, lineHeight: 0 }}><Trash2 size={12} /></button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* GESTOR DE CATEGORIAS PROPIAS (Lissette 2026-09-02, queja 7).
          Solo anadir y quitar: una categoria es un id y un nombre, y renombrar
          en sitio dejaria licencias apuntando a un nombre que ya no significa
          lo mismo. Quien quiera cambiarla, crea la nueva y mueve las licencias
          — que es explicito y se ve. */}
      {gestorDeTipos && !readOnly && (
        <Modal title={lang === 'es' ? 'Categorías de la agencia' : 'Agency categories'} onClose={() => setGestorDeTipos(false)}>
          <GestorDeCategorias
            propias={tiposPropios}
            deFabrica={getLicenseTypes(lang).map(t => t.id)}
            enUso={(licenses || []).map(l => l?.type).filter(Boolean)}
            lang={lang}
            onGuardar={guardarTiposPropios}
          />
        </Modal>
      )}
      {showForm && !readOnly && <LicenseForm initial={editLic || undefined} onSave={saveLic} onCancel={() => { setShowForm(false); setEditLic(null); }} lang={lang} defaultNpn={currentUser?.npn || ""} tiposPropios={tiposPropios} onNuevoTipo={crearTipoDesdeElFormulario} />}
      {renovarLic && !readOnly && <LicenseForm key={`renovar-${renovarLic.id}`} initial={renovarLic} renovando onSave={guardarRenovacion} onCancel={() => setRenovarLic(null)} lang={lang} defaultNpn={currentUser?.npn || ""} tiposPropios={tiposPropios} onNuevoTipo={crearTipoDesdeElFormulario} />}
      {notesFor && (() => {
        const list = parseLicenseNotes(notesFor.notes);
        const lt = getLicType(notesFor.type);
        const fmt = (iso) => { if (!iso) return ""; try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
        return (
          <Modal title={`📝 ${lang === "es" ? "Notas" : "Notes"} — ${lt.label} ${notesFor.state || ""}`} onClose={() => setNotesFor(null)} wide>
            <div style={{ display: "flex", flexDirection: "column", gap: ".6rem", maxHeight: "68vh", overflowY: "auto" }}>
              {list.length === 0 && <div style={{ fontSize: ".82rem", color: "var(--c-text3,#45688a)" }}>{lang === "es" ? "Sin notas." : "No notes."}</div>}
              {list.map((n) => (
                <div key={n.id} style={{ background: "var(--c-surface2,#112538)", border: "1px solid var(--c-border,#1a3550)", borderRadius: 10, padding: ".6rem .7rem" }}>
                  {n.text && <div style={{ fontSize: ".82rem", color: "var(--c-text,#c8dff0)", lineHeight: 1.5 }}><RichNote text={n.text} /></div>}
                  {(n.images || []).length > 0 && (
                    <div style={{ display: "flex", gap: ".45rem", flexWrap: "wrap", marginTop: n.text ? ".5rem" : 0 }}>
                      {n.images.map((im, i) => (
                        <img key={i} src={im.dataUrl} alt={im.name || ""} onClick={() => setLightbox({ src: im.dataUrl, name: im.name })}
                          style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 8, border: "1px solid var(--c-border,#1a3550)", cursor: "zoom-in" }} />
                      ))}
                    </div>
                  )}
                  {n.at && <div style={{ fontSize: ".62rem", color: "var(--c-text3,#45688a)", marginTop: ".4rem" }}>{fmt(n.at)}</div>}
                </div>
              ))}
            </div>
          </Modal>
        );
      })()}
      <ImageLightbox src={lightbox?.src} name={lightbox?.name} onClose={() => setLightbox(null)} />
    </div>
  );
}


/**
 * Anadir y quitar categorias propias de Licencias.
 *
 * QUITAR NO BORRA NADA. Una licencia guardada con esa categoria conserva su
 * valor; lo que pasa es que deja de tener nombre y la lista la ensena como
 * "Otro". Por eso el boton de quitar AVISA cuando hay licencias usandola y
 * dice cuantas: borrar a ciegas una categoria con veinte licencias detras es
 * el tipo de accion que no se puede deshacer con un ctrl-Z.
 */
function GestorDeCategorias({ propias, deFabrica, enUso, lang, onGuardar }) {
  const es = lang === 'es';
  const [nombre, setNombre] = useState('');
  const [guardando, setGuardando] = useState(false);
  const cuenta = (id) => enUso.filter(t => t === id).length;

  const anadir = async () => {
    const limpio = nombre.trim();
    if (!limpio) return;
    // El id sale del nombre, sin acentos ni espacios: viaja dentro de
    // License.type y se compara con los de fabrica.
    const id = idDeCategoria(limpio);
    if (!id) { toast.warn(es ? 'Ese nombre no deja hacer un identificador.' : 'That name cannot make an id.'); return; }
    if (deFabrica.includes(id) || propias.some(t => t.id === id)) {
      toast.warn(es ? 'Ya existe una categoría con ese nombre.' : 'A category with that name already exists.');
      return;
    }
    setGuardando(true);
    await onGuardar([...propias, { id, label: limpio, labelEs: limpio, icon: '🪪', color: '#6366f1' }]);
    setGuardando(false);
    setNombre('');
  };

  const quitar = async (t) => {
    const n = cuenta(t.id);
    const ok = await confirmDialog({
      message: n > 0
        ? (es
            ? `"${t.labelEs || t.label}" está en uso por ${n} licencia(s). Si la quitas, esas licencias NO se borran, pero pasarán a verse como "Otro". ¿Quitarla?`
            : `"${t.label || t.labelEs}" is used by ${n} license(s). Removing it does NOT delete them, but they will show as "Other". Remove it?`)
        : (es ? `¿Quitar la categoría "${t.labelEs || t.label}"?` : `Remove the category "${t.label || t.labelEs}"?`),
      danger: true,
      confirmLabel: es ? 'Quitar' : 'Remove',
    });
    if (!ok) return;
    await onGuardar(propias.filter(x => x.id !== t.id));
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', minWidth: 320 }}>
      <div style={{ fontSize: '.78rem', color: 'var(--c-text2,#7aa3c0)', lineHeight: 1.5 }}>
        {es
          ? 'Las categorías que crees aquí se suman a las de fábrica y las ve toda tu agencia. Se guardan: siguen ahí al recargar.'
          : 'Categories you create here are added to the built-in ones and shared with your whole agency. They persist across reloads.'}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input
          className="dkt-inp"
          value={nombre}
          maxLength={60}
          placeholder={es ? 'Nombre de la categoría' : 'Category name'}
          onChange={(e) => setNombre(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); anadir(); } }}
          style={{ flex: 1 }}
        />
        <button type="button" className="dkt-btn-s" onClick={anadir} disabled={guardando || !nombre.trim()}>
          <Plus size={14} />{es ? 'Añadir' : 'Add'}
        </button>
      </div>
      {propias.length === 0
        ? <div style={{ fontSize: '.78rem', color: 'var(--c-text3,#45688a)', fontStyle: 'italic' }}>
            {es ? 'Todavía no has creado ninguna.' : 'You have not created any yet.'}
          </div>
        : <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {propias.map((t) => (
              <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '.45rem .6rem', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, background: 'var(--c-surface2,#112538)' }}>
                <span>{t.icon || '🪪'}</span>
                <span style={{ flex: 1, fontSize: '.82rem', color: 'var(--c-text,#c8dff0)' }}>{(es ? (t.labelEs || t.label) : (t.label || t.labelEs))}</span>
                {cuenta(t.id) > 0 && (
                  <span style={{ fontSize: '.68rem', color: 'var(--c-text3,#45688a)' }}>
                    {cuenta(t.id)} {es ? 'en uso' : 'in use'}
                  </span>
                )}
                <button type="button" onClick={() => quitar(t)} title={es ? 'Quitar' : 'Remove'}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--c-red,#ff3d6b)', display: 'inline-flex', padding: 2 }}>
                  <Trash2 size={13} />
                </button>
              </div>
            ))}
          </div>}
    </div>
  );
}
